// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import i18n from "i18next";
import {
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  StopOutlined
} from "@ant-design/icons";
import { alignRight, orderDescend } from "../../../../utils/Constant";
import TableButton from "../../../components/TableButton";
import { MoreOutlined } from "@ant-design/icons/lib";
import { Button, Popover } from "antd";
import { showConfirm } from "../../../components/ConfirmModal";
import {
  requestDeleteSurvey,
  requestDuplicateSurvey,
  requestWorkflowSurvey
} from "../../../../state/survey/SurveysEffects";
import {
  BranchListDto,
  SurveyInfoResponseDTO
} from "../../../../api/backend-openapi";
import { NewsWorkflowStatus } from "../../../types/NewsTypes";
import { navigate } from "@reach/router";
import SurveyStatusTag from "../details/SurveyStatusTag";
import {
  canBeDeleted,
  getPublicationDateSurveyFrom,
  getStatusResponseSurveyFrom,
  getStatusSurveyFrom
} from "../../../../utils/SurveyUtils";
import { tooltipDateRender } from "../../../components/CustomTooltip";
import { SurveyWorkflowStatus } from "../../../types/SurveyTypes";
import SurveyResultsStatusTag from "../details/SurveyResultsStatusTag";
import SurveyVote from "../details/SurveyVote";
import { ColumnsType } from "antd/lib/table";

const popoverContent = (record: SurveyInfoResponseDTO, canDelete: boolean) => (
  <div className="d-inline-flex">
    <TableButton
      icon={<EyeOutlined />}
      tooltipTitle={i18n.t("surveys.table.tooltip.view")}
      onClick={() => {
        navigate(`/surveys/${record.id}`);
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />
    <TableButton
      icon={<CopyOutlined />}
      tooltipTitle={i18n.t("surveys.table.tooltip.duplicate")}
      onClick={() => {
        showConfirm(
          i18n.t("survey.confirmation.duplicate", { survey: record.title }),
          () => {
            requestDuplicateSurvey({
              id: record.id
            });
          }
        );
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />

    {record.status === NewsWorkflowStatus.PUBLISHED && (
      <TableButton
        icon={<StopOutlined />}
        buttonClassName="btn-secondary mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("surveys.table.confirmation.unpublish", {
              survey: record.title
            }),
            () => {
              requestWorkflowSurvey({
                id: record.id,
                dto: {
                  status: SurveyWorkflowStatus.VALIDATED
                }
              });
            }
          )
        }
        tooltipTitle={i18n.t("surveys.table.tooltip.unpublish")}
      />
    )}
    {canBeDeleted(record) && canDelete && (
      <TableButton
        icon={<DeleteOutlined />}
        buttonClassName="btn-danger btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("surveys.table.confirmation.delete", {
              survey: record.title
            }),
            () => {
              requestDeleteSurvey(record);
            }
          )
        }
        tooltipTitle={i18n.t("surveys.table.tooltip.delete")}
      />
    )}
  </div>
);

export const columns = (
  canDelete: boolean,
  branchList: BranchListDto
): ColumnsType<SurveyInfoResponseDTO> => [
  {
    title: i18n.t("surveys.table.name"),
    key: "title",
    dataIndex: "title",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: undefined
  },
  {
    title: i18n.t("surveys.table.branch"),
    key: "branch",
    dataIndex: "branch",
    ellipsis: false,
    render: (branch?: string) => {
      if (branch) {
        return branchList[branch]?.label || "";
      }
      return "";
    },
    defaultFilteredValue: undefined,
    filters: Object.keys(branchList).map(key => {
      return {
        text: branchList[key].label,
        value: key
      };
    }),
    filterMultiple: false,
    sorter: true
  },
  {
    title: i18n.t("surveys.table.type.title"),
    key: "type",
    dataIndex: "type",
    width: "6%",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: undefined,
    render: (record: string) => i18n.t(`surveys.table.type.${record}`)
  },
  {
    title: i18n.t("surveys.table.status"),
    key: "status",
    width: "16%",
    ellipsis: {
      showTitle: false
    },
    sorter: true,
    defaultSortOrder: undefined,
    render: (record: SurveyInfoResponseDTO) => {
      return record && <SurveyStatusTag status={getStatusSurveyFrom(record)} />;
    }
  },
  {
    title: i18n.t("surveys.table.results_status"),
    key: "results_status",
    width: "10%",
    ellipsis: false,
    render: (record: SurveyInfoResponseDTO) => {
      return (
        record && (
          <SurveyResultsStatusTag
            status={getStatusResponseSurveyFrom(record)}
          />
        )
      );
    }
  },
  {
    title: i18n.t("surveys.table.results"),
    key: "results",
    width: "9%",
    ellipsis: false,
    render: (record: SurveyInfoResponseDTO) => {
      return <SurveyVote record={record} />;
    }
  },
  {
    title: i18n.t("surveys.table.surveys_publication_date"),
    key: "publicationDate",
    width: "11%",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: undefined,
    render: (record: SurveyInfoResponseDTO) => {
      return tooltipDateRender(getPublicationDateSurveyFrom(record));
    }
  },
  {
    title: i18n.t("surveys.table.surveys_creation_date"),
    key: "creationDate",
    dataIndex: "creationDate",
    width: "11%",
    ellipsis: false,
    sorter: true,
    defaultSortOrder: orderDescend,
    render: tooltipDateRender
  },
  {
    title: i18n.t("surveys.table.action"),
    key: "action",
    fixed: alignRight,
    width: "9%",
    onCell: () => {
      return {
        onClick: (e: any) => e.stopPropagation()
      };
    },
    render: (record: SurveyInfoResponseDTO) => (
      <Popover
        content={popoverContent(record, canDelete)}
        placement="bottomRight"
        trigger="hover"
      >
        <Button
          icon={<MoreOutlined />}
          type="text"
          size="large"
          className="btn-list"
        />
      </Popover>
    )
  }
];
